import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InputType } from '@vinnova/ui';
import { ValidationService } from '@vinnova/validation';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { Country } from '../../../core/models/country.model';
import { CreateWorksiteModel } from '../../../core/models/create-worksite.model';
import { ListCompany } from '../../../core/models/list-company.model';
import { ListWorksite } from '../../../core/models/list-worksite.model';
import { WorksitesQuery } from '../../../core/queries/worksites.query';

@UntilDestroy()
@Component({
  selector: 'lib-create-worksite',
  templateUrl: './create-worksite.component.html',
  styleUrls: ['./create-worksite.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CreateWorksiteComponent implements OnInit {
  isWorksiteDuplicated = false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() form: FormGroup<any>;
  @Input() countries: Country[];
  @Input() selectedCompany: ListCompany;
  private _disabled: boolean;
  @Input() set disabled(value: boolean) {
    this._disabled = value;
    if (value) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  get disabled() {
    return this._disabled;
  }

  @Output() createWorksite = new EventEmitter<CreateWorksiteModel>();
  @Output() selectWorksite = new EventEmitter<ListWorksite>();
  @Output() duplicated = new EventEmitter<boolean>();

  duplicateWorksites$: Observable<ListWorksite[]>;
  countryErrorMessage$: Observable<string>;
  nameErrorMessage$: Observable<string>;
  phoneErrorMessage$: Observable<string>;
  commentErrorMessage$: Observable<string>;

  InputType = InputType;

  constructor(
    private validationService: ValidationService,
    private worksitesQuery: WorksitesQuery,
    private translocoService: TranslocoService,
  ) {}

  ngOnInit() {
    this.setupValueChangeListeners();
    this.setErrorMessage();
  }

  onForeignChange() {
    this.number.reset();
    if (this.isForeign.value) {
      this.country.reset();
    } else {
      this.country.reset('SE');
    }
  }

  resetForm() {
    this.form.reset({ country: 'SE' });
  }

  onSelectWorksite(worksite: ListWorksite) {
    this.selectWorksite.emit(worksite);
  }

  get addressForm() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.form.get('address') as FormGroup<any>;
  }

  get country() {
    return this.form.get('address').get('country') as FormControl<string>;
  }

  get isForeign() {
    return this.form.get('isForeign') as FormControl<string>;
  }

  get number() {
    return this.form.get('number') as FormControl<string>;
  }

  get name() {
    return this.form.get('name') as FormControl<string>;
  }

  get comment() {
    return this.form.get('comment') as FormControl<string>;
  }

  get phone() {
    return this.form.get('phone') as FormControl<string>;
  }

  get municipality() {
    return this.addressForm.get('municipalityCode') as FormControl<string>;
  }

  get county() {
    return this.addressForm.get('countyCode') as FormControl<string>;
  }

  private setupValueChangeListeners() {
    this.country.valueChanges
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((value) => {
        /*
         * När vi kommer till valueChanges så har inte värdet ändrats än på formcontrollen
         * Vi gör en patchValue på country-controllen här för att när
         * vi kollar om valideringen på zipcode behöver nya värdet finnas på country
         * Undviker oändlig loop genom distinctUntilChanged
         */
        this.country.patchValue(value);
        this.addressForm.get('zipCode').updateValueAndValidity();
      });

    this.isForeign.valueChanges
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((value) => {
        this.municipality.reset();
        this.county.reset();
        if (value) {
          this.municipality.disable();
          this.county.disable();
        } else {
          this.municipality.enable();
          this.county.enable();
        }
      });

    this.duplicateWorksites$ = this.number.valueChanges.pipe(
      debounceTime(1000),
      distinctUntilChanged(),
      switchMap((value) => {
        return value
          ? this.worksitesQuery.search(
              undefined,
              undefined,
              undefined,
              this.form.value.address.country,
              value,
            )
          : of(undefined);
      }),
      map((searchWorksitesResult) => {
        const listWorksites = [];
        if (searchWorksitesResult) {
          for (const listCompany of searchWorksitesResult.result) {
            for (const listWorksite of listCompany.worksites) {
              listWorksites.push(listWorksite);
            }
          }
          this.isWorksiteDuplicated =
            listWorksites && listWorksites.length > 0 ? true : false;
        } else {
          this.isWorksiteDuplicated = false;
        }

        this.duplicated.emit(this.isWorksiteDuplicated);
        return listWorksites;
      }),
    );
  }

  setErrorMessage() {
    this.countryErrorMessage$ = this.validationService
      .isInvalid(this.country)
      .pipe(
        map((invalid) => {
          if (!invalid) {
            return;
          }

          if (this.country.errors.required) {
            return this.translocoService.translate('required');
          }
        }),
      );

    this.nameErrorMessage$ = this.validationService.isInvalid(this.name).pipe(
      map((invalid) => {
        if (!invalid) {
          return;
        }

        if (this.name.errors.required) {
          return this.translocoService.translate('required');
        }

        if (this.name.errors.maxlength) {
          return this.translocoService.translate('maxlength');
        }
      }),
    );

    this.phoneErrorMessage$ = this.validationService.isInvalid(this.phone).pipe(
      map((invalid) => {
        if (!invalid) {
          return;
        }

        if (this.phone.errors.validPhoneNumber) {
          return this.translocoService.translate('phone_invalid');
        }
      }),
    );

    this.commentErrorMessage$ = this.validationService
      .isInvalid(this.comment)
      .pipe(
        map((invalid) => {
          if (!invalid) {
            return;
          }

          if (this.comment.errors.required) {
            return this.translocoService.translate('required');
          }

          if (this.comment.errors.maxlength) {
            return this.translocoService.translate('maxlength');
          }
        }),
      );
  }
}
